import React from 'react';
import { motion } from 'framer-motion';

export interface SwitchProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  id?: string;
  /** Accessible label for screen readers when no visible label is wired via aria-labelledby. */
  'aria-label'?: string;
  /** Size of the track. Default 'md' (36×20). 'sm' = 30×17. */
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * Switch — the app's standard on/off toggle. Track flips between the primary
 * accent (on) and a muted surface tint (off) via semantic tokens, so it follows
 * light/dark automatically. The thumb slides with a spring for tactile feedback.
 */
export default function Switch({
  checked,
  onChange,
  disabled = false,
  id,
  size = 'md',
  className = '',
  ...rest
}: SwitchProps) {
  const dims =
    size === 'sm'
      ? { w: 30, h: 17, thumb: 13 }
      : { w: 36, h: 20, thumb: 16 };

  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={rest['aria-label']}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      style={{ width: dims.w, height: dims.h }}
      className={`relative inline-flex shrink-0 items-center rounded-full p-[2px] cursor-pointer transition-colors duration-200 ease-out outline-none focus-visible:ring-2 focus-visible:ring-primary-200 disabled:opacity-60 disabled:cursor-not-allowed ${
        checked ? 'bg-primary-500' : 'bg-text-tertiary/40'
      } ${className}`}
    >
      <motion.span
        layout
        transition={{ type: 'spring', stiffness: 700, damping: 30 }}
        style={{
          width: dims.thumb,
          height: dims.thumb,
          marginLeft: checked ? 'auto' : 0,
        }}
        className="block rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.25)]"
      />
    </button>
  );
}
